/**
 * Roles Hooks
 *
 * React hooks for role management using TanStack Query
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiResponse, ApiListResponse } from '@/lib/api';
import {
    useAccessControlStore,
    type Role,
    type AggregateCount,
} from '@/stores/access-control';
import { buildQueryString } from './query-utils';

// ============== Types ==============

export interface RoleInput {
    name: string;
    icon?: string;
    description?: string;
    parent?: string | null;
}

// Response type for roles with aggregation
interface RoleWithAggregates extends Omit<Role, 'userCount' | 'policyCount'> {
    users?: AggregateCount[];
    policies?: AggregateCount[];
}

// ============== Hooks ==============

/**
 * Fetch all roles with user and policy counts
 */
export function useRoles() {
    const { setRoles, setRolesLoading } = useAccessControlStore();

    return useQuery({
        queryKey: ['roles'],
        queryFn: async () => {
            setRolesLoading(true);
            try {
                // Build query with deep aggregation like reference CMS
                const queryParams = buildQueryString({
                    limit: -1,
                    fields: ['id', 'name', 'icon', 'description', 'parent', 'users', 'policies'],
                    deep: {
                        users: {
                            _aggregate: { count: 'id' },
                        },
                        policies: {
                            _aggregate: { count: 'id' },
                        },
                    },
                    sort: 'name',
                });

                const response = await api.get<ApiListResponse<RoleWithAggregates>>(`/roles?${queryParams}`);
                const rawRoles = response.data || [];

                // Transform aggregation results to counts
                const roles: Role[] = rawRoles.map((role) => ({
                    ...role,
                    userCount: role.users?.[0]?.count?.id ?? 0,
                    policyCount: role.policies?.[0]?.count?.id ?? 0,
                }));

                setRoles(roles);
                return roles;
            } finally {
                setRolesLoading(false);
            }
        },
    });
}

/**
 * Fetch single role by ID
 */
export function useRole(id: string | undefined) {
    const { setSelectedRole } = useAccessControlStore();

    return useQuery({
        queryKey: ['roles', id],
        queryFn: async () => {
            if (!id) return null;
            const response = await api.get<ApiResponse<Role>>(`/roles/${id}`);
            const role = response.data || null;
            setSelectedRole(role);
            return role;
        },
        enabled: !!id,
    });
}

/**
 * Create a new role
 */
export function useCreateRole() {
    const queryClient = useQueryClient();
    const { addRole } = useAccessControlStore();

    return useMutation({
        mutationFn: async (data: RoleInput) => {
            const response = await api.post<ApiResponse<Role>>('/roles', data);
            return response.data;
        },
        onSuccess: (role) => {
            if (role) addRole(role);
            queryClient.invalidateQueries({ queryKey: ['roles'] });
        },
    });
}

/**
 * Update a role
 */
export function useUpdateRole() {
    const queryClient = useQueryClient();
    const { updateRole } = useAccessControlStore();

    return useMutation({
        mutationFn: async ({ id, data }: { id: string; data: Partial<RoleInput> }) => {
            const response = await api.patch<ApiResponse<Role>>(`/roles/${id}`, data);
            return response.data;
        },
        onSuccess: (role) => {
            if (role) updateRole(role.id, role);
            queryClient.invalidateQueries({ queryKey: ['roles'] });
        },
    });
}

/**
 * Delete a role
 */
export function useDeleteRole() {
    const queryClient = useQueryClient();
    const { removeRole } = useAccessControlStore();

    return useMutation({
        mutationFn: async (id: string) => {
            await api.delete(`/roles/${id}`);
            return id;
        },
        onSuccess: (id) => {
            removeRole(id);
            queryClient.invalidateQueries({ queryKey: ['roles'] });
        },
    });
}
